import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid2 as Grid,
	Box,
	Typography,
	FormControl,
	Button,
	IconButton,
	TextField,
	InputLabel,
	Select,
	MenuItem,
	FormLabel,
	FormHelperText,
	ToggleButton,
	ToggleButtonGroup,
	InputAdornment,
	useMediaQuery,
	useTheme,
} from "@mui/material";


import { sub, parse, format } from "date-fns";
import { enAU } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIcon } from "@mui/x-date-pickers/icons"; 
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { makeStyles, withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { LargeActionButton } from "../Common/LargeActionButton";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import { getAmPmTimes, getCurrentTime } from "../../util/dateUtil";
import { arrivalCheckinMethod } from "../../util/receiveStatus";
import PageContext from "../../components/Common/PageContext";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		marginTop: "4px",
	},
	datePickerCell: {
		paddingRight: "2px",
	},
	timePickerCell: {
		paddingleft: "2px",
	},
	datePicker: {
		"& .MuiInputBase-root": {
			height: "56px",
		},
		"& .MuiInputLabel-marginDense": {
			transform: "translate(14px, 20px) scale(1)",
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)",
		},
	},
	inputLabel: {
		marginBottom: "8px",
	},
}));

const OperationButton = withStyles({
	root: {
		height: "56px",
		width: "56px",
		minWidth: "56px",
	},
	contained: {
		padding: 0,
	},
})(Button);

const StyledToggleButtonGroup = withStyles((theme) => ({
	grouped: {
		color: theme.palette.text.primary,
		border: `1px solid ${theme.palette.divider}`,
	},
}))(ToggleButtonGroup);

const StyledToggleButton = withStyles((theme) => ({
	root: {
		width: "33.33%",
		textTransform: "none",
	},
	selected: {
		backgroundColor: `${theme.palette.primary.main} !important`,
		color: `${theme.palette.primary.fontColor} !important`,
	},
}))(ToggleButton);

export default function ArrivalsCheckinDialog(props) {
	const { isOpen, onClose, onCompleteCheckin, purchaseOrders, scannedConsignmentNumber, isBlocking } = props;
	const history = useHistory();
	const requiredErrorMessage = "Required";
	const invalidConsignmentNumber = "Cannot be the same as PO or invoice number";
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const minArrivalDate = sub(new Date(), { days: 14 });
	const defaultTime = getCurrentTime();
	const [form, setValue] = useState({
		arrivalDate: { value: new Date(), error: false, errorMessage: "" },
		arrivalTime: { value: defaultTime, error: false, errorMessage: "" },
		consignmentNumber: { value: "", error: false, errorMessage: "" },
		carrier: { value: "", error: false, errorMessage: "" },
		receivedQty: { value: 0, error: false, errorMessage: "" },
		inFull: { value: "", error: false, errorMessage: "" },
	});
	const unblockRef = useRef(null);

	const [pageContext] = React.useContext(PageContext);
	const { carriers: couriers } = pageContext;

	const times = getAmPmTimes();

	const isNullOrUndefinedOrEmpty = (value) => {
		return value === null || value === undefined || value === "";
	};
	const handleClickClose = () => {
		onClose(true);
	};
	const createFormValue = useCallback((value) => {
		const formValue = { error: false, errorMessage: "", value };
		if (isNullOrUndefinedOrEmpty(value)) {
			formValue.error = true;
			formValue.errorMessage = requiredErrorMessage;
		}
		return formValue;
	}, []);
	const handleArrivalDateChange = (date) => {
		const formValue = createFormValue(date);
		setValue({ ...form, arrivalDate: formValue });
	};
	const handleArrivalTimeChange = (event) => {
		const formValue = createFormValue(event.target.value);
		setValue({ ...form, arrivalTime: formValue });
	};
	const isValidConsignmentNumber = useCallback(
		(value) => {
			// consignment number cannot be similar to PO number on INV number;
			const po = purchaseOrders.find((i) => i.po === value || i.invoices.includes(value));
			return !po;
		},
		[purchaseOrders]
	);
	const handleConsignmentNumberChange = (event) => {
		setConsignmentNumber(event.target.value);
	};
	const setConsignmentNumber = useCallback(
		(value) => {
			if (value === null || value === undefined) return;

			const formValue = createFormValue(value);
			if (!formValue.error && !isValidConsignmentNumber(formValue.value)) {
				formValue.errorMessage = "Cannot be the same as PO or invoice number";
				formValue.error = true;
			}
			setValue({ ...form, consignmentNumber: formValue });
		},
		[createFormValue, form, isValidConsignmentNumber]
	);
	const handleCarrierChange = (event) => {
		const formValue = createFormValue(event.target.value);
		setValue({ ...form, carrier: formValue });
	};
	const handleReceivedQtyChange = (event) => {
		const formValue = createFormValue(event.target.value);

		if (formValue.error) {
			formValue.value = 0;
			setValue({ ...form, receivedQty: formValue });
			return;
		}

		const inputValue = formValue.value.replace(/^0+/, "");
		if (!inputValue) {
			formValue.value = 0;
			setValue({ ...form, receivedQty: formValue });
			return;
		}
		formValue.value = parseInt(inputValue);
		setValue({ ...form, receivedQty: formValue });
	};
	const handleIncreaseReceivedQty = () => {
		form.receivedQty.value = form.receivedQty.value + 1;
		form.receivedQty.error = false;
		form.receivedQty.errorMessage = "";
		setValue({ ...form });
	};
	const handleDecreaseReceivedQty = () => {
		if (form.receivedQty.value >= 1) {
			form.receivedQty.value = form.receivedQty.value - 1;
			form.receivedQty.error = form.receivedQty.value === 0;
			form.receivedQty.errorMessage = form.receivedQty.value === 0 ? requiredErrorMessage : "";
			setValue({ ...form });
		}
	};
	const handleInFullChange = (_, value) => {
		const formValue = createFormValue(value);
		setValue({ ...form, inFull: formValue });
	};
	const handleCheckInShipment = () => {
		if (validate()) {
			const arrivalDateTimeString = `${format(form.arrivalDate.value, "dd-MM-yyyy")} ${form.arrivalTime.value}`;
			const arrivalDateTime = parse(arrivalDateTimeString, "dd-MM-yyyy hh:mm a", form.arrivalDate.value);
			const arrivalData = {
				dateArrived: arrivalDateTime,
				consignmentNumber: form.consignmentNumber.value,
				numberReceived: form.receivedQty.value,
				carrier: form.carrier.value,
				inFull: form.inFull.value,
				createdByUserName: null, // To be set on parent page
				createdByUserId: null, // To be set on parent page
				checkinMethod: arrivalCheckinMethod.manual,
				asnId: null,
				packages: [],
			};
			onCompleteCheckin(arrivalData);
		}
	};
	const validate = () => {
		let isValid = true;
		for (var prop in form) {
			const formValue = createFormValue(form[prop].value);

			if (prop === "receivedQty" && formValue.value < 1) {
				formValue.error = true;
				formValue.errorMessage = requiredErrorMessage;
			} else if (prop === "consignmentNumber" && !isValidConsignmentNumber(formValue.value)) {
				formValue.error = true;
				formValue.errorMessage = invalidConsignmentNumber;
			}

			if (formValue.error) isValid = false;
			form[prop] = formValue;
		}
		setValue({ ...form });
		return isValid;
	};

	useEffect(() => {
		setConsignmentNumber(scannedConsignmentNumber);
	}, [scannedConsignmentNumber, setConsignmentNumber]);

	useEffect(() => {
		if (isOpen) {
			const blockNavigation = () => {
				if (unblockRef.current) {
					unblockRef.current(); // Unblock any existing block before setting a new one
				}
				const unblockNavigation = history.block((location, action) => {
					if (action === "POP") {
						if (onClose(true)) {
							unblockNavigation();
						} else {
							blockNavigation();
						}
						return false;
					}
					return undefined;
				}) as () => void;
				unblockRef.current = unblockNavigation;
			};

			blockNavigation();

			return () => {
				if (unblockRef.current) {
					unblockRef.current(); // Clean up the block
				}
			};
		}
	}, [history, onClose, isBlocking, isOpen]);

	useEffect(() => {
		if (!isOpen) {
			setValue({
				arrivalDate: { value: new Date(), error: false, errorMessage: "" },
				arrivalTime: { value: defaultTime, error: false, errorMessage: "" },
				consignmentNumber: { value: "", error: false, errorMessage: "" },
				carrier: { value: "", error: false, errorMessage: "" },
				receivedQty: { value: 0, error: false, errorMessage: "" },
				inFull: { value: "", error: false, errorMessage: "" },
			});
		}
	}, [isOpen, setValue, defaultTime]);

	return (
		<Dialog fullScreen open={isOpen} onClose={handleClickClose} scroll="body">
			<DialogTitle>
				<Grid container spacing={0}>
					<Grid size={11}>
						<Typography variant="subtitle1" display="block" className={classes.dialogTitle}>
							Check in supplier shipment
						</Typography>
					</Grid>
					<Grid size={1} container justifyContent="flex-end">
						<IconButton onClick={handleClickClose} size="small" edge="end">
							<CloseIcon color="primary" />
						</IconButton>
					</Grid>
				</Grid>
			</DialogTitle>
			<DialogContent>
				<Grid container justifyContent="center" direction="column" spacing={2}>
					<Grid container size={12} sx={{ mt: 1 }}>
						<Grid size={6} className={classes.datePickerCell}>
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
							<DatePicker
								label="Arrival Date"
								format="dd/MM/yyyy"
								value={form.arrivalDate.value}
								disableFuture
								minDate={minArrivalDate}
								closeOnSelect
							slots={{ actionBar: DatePickerMobileActionBar }}
							slotProps={{
								textField: {
									variant: "outlined",
									fullWidth: true,
									error: form.arrivalDate.error,
									helperText: form.arrivalDate.errorMessage,
									id: "arrival-date",
									required: true,
								},
								field: { InputProps: { endAdornment: <InputAdornment position="end"><CalendarIcon fontSize="small" /></InputAdornment> } },
								actionBar: {
									actions: isMobile ? ["cancel", "accept"] : [],
									onSetDefaultDate: () => {
										if (!form.arrivalDate.value) handleArrivalDateChange(new Date());
									},
								} as any,
								}}
								onChange={handleArrivalDateChange}
							/>
							</LocalizationProvider>
						</Grid>
						<Grid size={6} className={classes.timePickerCell}>
							<FormControl variant="outlined" fullWidth error={form.arrivalTime.error}>
								<InputLabel id="arrival-time-input-label">Time *</InputLabel>
								<Select
									value={form.arrivalTime.value}
									label="Time"
									labelId="arrival-time-input-label"
									id="arrival-time-input"
									onChange={handleArrivalTimeChange}
									required
								>
									{times.map((time) => (
										<MenuItem key={time} value={time}>
											{time}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>{form.arrivalTime.errorMessage}</FormHelperText>
							</FormControl>
						</Grid>
					</Grid>
					<Grid size={12}>
						<TextField
							error={form.consignmentNumber.error}
							helperText={form.consignmentNumber.errorMessage}
							value={form.consignmentNumber.value}
							id="consignment-number"
							label="Consignment number"
							variant="outlined"
							fullWidth
							onChange={handleConsignmentNumberChange}
							required
						/>
					</Grid>
					<Grid size={12}>
						<FormControl variant="outlined" fullWidth error={form.carrier.error}>
							<InputLabel id="carrier-input-label">Carrier *</InputLabel>
							<Select
								label="Carrier"
								labelId="carrier-input-label"
								id="carrier-input"
								value={form.carrier.value}
								onChange={handleCarrierChange}
								required
							>
								{couriers.map((courier) => (
									<MenuItem key={courier} value={courier}>
										{courier}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>{form.carrier.errorMessage}</FormHelperText>
						</FormControl>
					</Grid>
					<Grid size={12}>
						<FormLabel className={classes.inputLabel}>Cartons received</FormLabel>
					</Grid>
					<Grid container size={12} spacing={0}>
						<Box display="flex" flexDirection="row" flexGrow={1}>
							<Box display="flex" justifyContent="flex-start">
								<OperationButton
									fullWidth
									variant="contained"
									color="secondary"
									onClick={handleDecreaseReceivedQty}
									disabled={form.receivedQty.value < 1}
								>
									<RemoveIcon />
								</OperationButton>
							</Box>
							<Box flexGrow={2} mr="12px" ml="12px">
								<TextField
									type="number"
									id="cartons-received"
									label="Count"
									variant="outlined"
									error={form.receivedQty.error}
									helperText={form.receivedQty.errorMessage}
									value={form.receivedQty.value}
									slotProps={{ htmlInput: { min: 0 } }}
									onChange={handleReceivedQtyChange}
									fullWidth
									required
								/>
							</Box>
							<Box display="flex" justifyContent="flex-end">
								<OperationButton fullWidth variant="contained" color="secondary" onClick={handleIncreaseReceivedQty}>
									<AddIcon />
								</OperationButton>
							</Box>
						</Box>
					</Grid>
					<Grid size={12}>
						<FormControl fullWidth error={form.inFull.error}>
							<FormLabel className={classes.inputLabel}>Was the shipment complete? *</FormLabel>
							<StyledToggleButtonGroup exclusive value={form.inFull.value} onChange={handleInFullChange}>
								<StyledToggleButton value="Unknown">Unknown</StyledToggleButton>
								<StyledToggleButton value="Incomplete">Incomplete</StyledToggleButton>
								<StyledToggleButton value="Complete">Complete</StyledToggleButton>
							</StyledToggleButtonGroup>
							<FormHelperText>{form.inFull.errorMessage}</FormHelperText>
						</FormControl>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Grid container justifyContent="flex-start" size="grow" sx={{ width: '100%' }}>
					<LargeActionButton onClick={handleCheckInShipment}>Check in shipment</LargeActionButton>
				</Grid>
			</DialogActions>
		</Dialog>
	);
}

ArrivalsCheckinDialog.propTypes = {
	purchaseOrders: PropTypes.array.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onCompleteCheckin: PropTypes.func.isRequired,
	scannedConsignmentNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
	isBlocking: PropTypes.bool.isRequired,
};
